import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { Audio } from "expo-av";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  View,
  Text,
  FlatList,
  Share,
  Alert,
  Animated,
  Easing,
  Platform,
  ActivityIndicator,
  Image,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import { Dimensions } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

// Get screen dimensions for responsive design
const { width: screenWidth, height: screenHeight } = Dimensions.get("window");

// Font scaling utility for better mobile readability
const scaleFont = (size: number) => {
  const scale = screenWidth / 375; // Base on iPhone X width
  const newSize = size * scale;
  return Math.max(newSize, size * 0.85); // Minimum 85% of original size
};

// Responsive measurements
const trophyMaxHeight = Math.min(screenHeight * 0.2, 150); // Max 150px
const isSmallScreen = screenWidth < 375;
const isMediumScreen = screenWidth >= 375 && screenWidth < 414;
const isLargeScreen = screenWidth >= 414;

// Responsive padding
const getResponsivePadding = () => {
  if (isSmallScreen) return 12;
  if (isMediumScreen) return 16;
  return 20;
};

// Responsive max width
const getMaxContentWidth = () => {
  if (isSmallScreen) return '95%';
  if (isMediumScreen) return '90%';
  return Math.min(screenWidth * 0.85, 480);
};

// Defensive asset imports (top-level)
import avatarDefault from "../../../../assets/avatar-default.png";
import badgeLottieCrownWin from "../../../../assets/lottie/badges/crown-win.json";
import badgeLottieFastest from "../../../../assets/lottie/badges/fastest.json";
import badgeLottieQuizKing from "../../../../assets/lottie/badges/quiz-king.json";
import badgeLottieUnlockFire from "../../../../assets/lottie/badges/unlock-fire.json";
import badgeLottieUnlockScroll from "../../../../assets/lottie/badges/unlock-scroll.json";
import confettiLottie from "../../../../assets/lottie/confetti.json";
import trophyLottieTie from "../../../../assets/lottie/trophies/double-trophy.json";
import trophyLottieWinner from "../../../../assets/lottie/trophies/gold-trophy.json";
import trophyLottieLoser from "../../../../assets/lottie/trophies/silver-trophy.json";
import Confetti from "../../../components/Confetti";
import { useMultiplayerStore } from "../../../features/quiz-core/multiplayerStore";
import { useMultiplayerStore as multiplayerStoreInstance } from "../../../features/quiz-core/multiplayerStore";
import { useUserProfile } from "../../../features/quiz-core/useStore";
import { t } from "../../../i18n/translations";
import { maybeRequestReviewAfterMatch } from "../../../lib/appReview";
import { maybeRequestPushAfterQuiz } from "../../../lib/pushPrompt";
import { RootStackParamList } from "../../../navigation/types";
import { batchProcessGamification } from "../../../shared/services/batchGamificationService";
import {
  checkQuizAchievements,
  getUserXP,
  getUserStreak,
  getUserAchievements,
  awardXP,
  getRankFromLevel,
  type UserXP,
  type UserStreak,
  type UserAchievement
} from "../../../shared/services/gamificationService";
import { syncQuizToPGS, isFallbackResult } from "../../../shared/services/pgsAchievementBridge";
import { triggerAutoSave } from "../../../shared/services/pgsCloudSaveService";
import {
  processEnhancedQuizCompletion,
  generateKnowledgeMap,
  generateAchievementShareText,
  getUserSubscription,
  getExpertModeConfig,
  type PremiumSubscription 
} from "../../../shared/services/premiumLevelingService";
import {
  fetchRoomPlayersWithScores,
  subscribeToRoomPlayersRealtime,
} from "../../../shared/services/supabaseService";
import { useLanguageStore } from "../../quiz-core/languageStore";
import MultiplayerActionButton from "../components/MultiplayerActionButton";
import MultiplayerPlayerCard from "../components/MultiplayerPlayerCard";
import ResultsActionsBlock from "../components/ResultsActionsBlock";
import ResultsConfettiOverlay from "../components/ResultsConfettiOverlay";
import ResultsHeaderBlock from "../components/ResultsHeaderBlock";
import ResultsLeaderboard from "../components/ResultsLeaderboard";
import { RESULTS_SHARED_WIDTH } from "../components/ResultsSharedWidth";
import { useRoomReset } from "../hooks/useRoomReset";
import { useSessionKeepalive } from '../hooks/useSessionKeepalive';
import { resolveRoomGroupMeta } from "../utils/groupMeta";

import { supabase as supabaseLib } from "./../../../lib/supabase";

const applauseSound: any = require("../../../../assets/applause.mp3");
const winSound: any = require("../../../../assets/results/win.mp3");
// PlayerCard and related inline logic removed. Use MultiplayerPlayerCard only via imports and usage in render.

const IQES_RESULTS = {
  background: "#090317",
  panel: "#0B1026",
  panelDeep: "#070A18",
  panelLift: "#151B37",
  gold: "#F8C84E",
  goldSoft: "#FFE78A",
  purpleDeep: "#21103D",
  ivory: "#FFF7DF",
  text: "#F9F0D6",
  muted: "#C9BEDA",
  border: "rgba(248, 200, 78, 0.42)",
  borderStrong: "rgba(248, 200, 78, 0.72)",
  green: "#7FE08E",
  red: "#EF6B63",
};

// Stricter Lottie source validation
function isValidLottieSource(source: any) {
  if (typeof source === "number") return true; // require()
  if (typeof source === "string" && source.endsWith(".json")) return true;
  if (
    typeof source === "object" &&
    source !== null &&
    !Array.isArray(source) &&
    typeof source.v === "string" &&
    Array.isArray(source.layers)
  ) {
    return true;
  }
  return false;
}

// Define trophyLotties and badgeLotties objects for use throughout the file
const trophyLotties = {
  tie: trophyLottieTie,
  winner: trophyLottieWinner,
  loser: trophyLottieLoser,
};
const badgeLotties: { [key: string]: any } = {
  "quiz-king": badgeLottieQuizKing,
  fastest: badgeLottieFastest,
  "crown-win": badgeLottieCrownWin,
  "unlock-scroll": badgeLottieUnlockScroll,
  "unlock-fire": badgeLottieUnlockFire,
};

type LeaderboardPlayerResult = {
  id?: string;
  user_id: string;
  name?: string | null;
  nickname?: string | null;
  avatar_url?: string | null;
  score: number;
  correct: number;
  totalQuestions: number;
  accuracy: number;
  time: string | null;
  timeTakenSeconds: number | null;
  finished_at?: string | null;
  joined_at?: string | null;
};

const formatResultTime = (timeTakenSeconds: number | null): string | null => {
  if (typeof timeTakenSeconds !== "number" || timeTakenSeconds < 0) {
    return null;
  }

  const minutes = Math.floor(timeTakenSeconds / 60);
  const seconds = timeTakenSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

function MultiplayerResultsScreen() {
  // Rematch flow now uses navigation, not modal.

  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "MultiplayerResults">>();
  const routeRoomId = route.params?.roomId;
  const { currentRoom } = useMultiplayerStore();
  const [hydratedRoom, setHydratedRoom] = useState<any | null>(null);
  const effectiveRoom = useMemo(
    () => (currentRoom?.id === routeRoomId ? currentRoom : hydratedRoom),
    [currentRoom, hydratedRoom, routeRoomId],
  );
  
  // Keep session alive during results screen
  useSessionKeepalive(true);
  // Modular room reset
  const { resetRoom, resetting, resetError } = useRoomReset(
    effectiveRoom?.id || routeRoomId || null,
  );
  const profile = useUserProfile();
  const { language } = useLanguageStore();

  // Leaderboard state: fetched from DB
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [isLoadingResults, setIsLoadingResults] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Gamification state
  const [xpEarned, setXpEarned] = useState<number>(0);
  const [levelUp, setLevelUp] = useState<boolean>(false);
  const [newLevel, setNewLevel] = useState<number>(1);
  const [newRank, setNewRank] = useState<string>('Beginner');
  const [newAchievements, setNewAchievements] = useState<UserAchievement[]>([]);
  const [streakInfo, setStreakInfo] = useState<UserStreak | null>(null);
  const [beforeXP, setBeforeXP] = useState<UserXP | null>(null);
  const [afterXP, setAfterXP] = useState<UserXP | null>(null);
  const [gamificationLoading, setGamificationLoading] = useState<boolean>(true);
  const [gamificationError, setGamificationError] = useState<string | null>(null);

  // Premium Enhancement States
  const [shareableContent, setShareableContent] = useState<string | null>(null);

  const roomMeta = useMemo(
    () => resolveRoomGroupMeta(effectiveRoom ?? currentRoom ?? null, language),
    [effectiveRoom, currentRoom, language],
  );
  const isCurrentUserHost = useMemo(
    () => Boolean(route.params?.isHost || (profile?.id && effectiveRoom?.host_id && profile.id === effectiveRoom.host_id)),
    [effectiveRoom?.host_id, profile?.id, route.params?.isHost],
  );
  const hasAutoNavigatedToRoomRef = useRef(false);

  useEffect(() => {
    let active = true;

    async function hydrateRoomFromRoute() {
      if (!routeRoomId) {
        return;
      }

      if (currentRoom?.id === routeRoomId) {
        setHydratedRoom(null);
        return;
      }

      try {
        const { data, error } = await supabaseLib
          .from('multiplayer_game_rooms')
          .select('*')
          .eq('id', routeRoomId)
          .single();

        if (!active) return;

        if (error) {
          console.warn('[MultiplayerResultsScreen] Failed to hydrate room from route:', error);
          return;
        }

        if (data) {
          setHydratedRoom(data);
        }
      } catch (error) {
        console.warn('[MultiplayerResultsScreen] Room hydration error:', error);
      }
    }

    hydrateRoomFromRoute();

    return () => {
      active = false;
    };
  }, [currentRoom?.id, routeRoomId]);

  // Fetch leaderboard and subscribe to realtime changes
  // Retryable leaderboard fetch
  const fetchInitialLeaderboard = async () => {
    if (!routeRoomId) {
      setFetchError(t('results.noRoomIdAvailable', language));
      return;
    }
    setIsLoadingResults(true);
    setFetchError(null);
    try {
      const data = await fetchRoomPlayersWithScores(routeRoomId);
      setLeaderboard(data);
      setIsLoadingResults(false);
      setFetchError(null);
    } catch (error: any) {
      setIsLoadingResults(false);
      setFetchError(error?.message || 'Failed to fetch results.');
    }
  };

  // Initial leaderboard fetch and realtime subscription
  useEffect(() => {
    let channel: any = null;
    let mounted = true;

    async function fetchAndSubscribe() {
      if (!routeRoomId || !profile?.id) {
        return;
      }

      setIsLoadingResults(true);
      setFetchError(null);

      try {
        // Initial fetch
        const data = await fetchRoomPlayersWithScores(routeRoomId);

        if (!mounted) return; // Component unmounted, don't update state

        setLeaderboard(data);
        setIsLoadingResults(false);
        setFetchError(null);

        // Realtime subscription - only if component is still mounted
        if (mounted) {
          channel = subscribeToRoomPlayersRealtime(routeRoomId, async () => {
            if (!mounted) return; // Don't update if unmounted

            try {
              const updated = await fetchRoomPlayersWithScores(routeRoomId);

              if (mounted) {
                setLeaderboard(updated);
              }
            } catch (error) {
              console.warn('[MultiplayerResults] Realtime update error:', error);
            }
          });
        }
      } catch (error: any) {
        if (mounted) {
          setIsLoadingResults(false);
          setFetchError(error?.message || 'Failed to fetch results.');
        }

        console.error(
          `[MultiplayerResultsScreen][${new Date().toISOString()}] Failed to fetch/subscribe leaderboard:`,
          error,
        );
      }
    }

    fetchAndSubscribe();

    return () => {
      mounted = false;
      if (channel && channel.unsubscribe) {
        channel.unsubscribe();
      }
    };
  }, [routeRoomId, profile?.id]);

  // Processing guard to prevent infinite loops
  const [isProcessingRewards, setIsProcessingRewards] = useState(false);

  // Timeout recovery mechanism to prevent permanent stuck states
  useEffect(() => {
    if (isProcessingRewards) {
      const timeoutId = setTimeout(() => {
        setIsProcessingRewards(false);
        setGamificationLoading(false);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeoutId);
    }
  }, [isProcessingRewards]);

  // Single trigger for gamification processing - simplified to prevent infinite loops
  useEffect(() => {
    // Simple check: only process if we have data and haven't processed yet
    const shouldProcessRewards = leaderboard.length > 0 && profile?.id && effectiveRoom &&
      !isLoadingResults && !isProcessingRewards && xpEarned === 0;

    if (shouldProcessRewards) {
      processMultiplayerResults();
    }
  }, [leaderboard.length, profile?.id, effectiveRoom?.id, isLoadingResults]); // Simplified dependencies

  const handleRetryFetch = () => {
    fetchInitialLeaderboard();
  };

  const navigateToGameRoom = useCallback(() => {
    if (!routeRoomId || hasAutoNavigatedToRoomRef.current) {
      return;
    }

    hasAutoNavigatedToRoomRef.current = true;
    navigation.replace("GameRoom", {
      roomId: routeRoomId,
      familyId: effectiveRoom?.family_id || route.params?.familyId || undefined,
      familyName: effectiveRoom?.family_name || route.params?.familyName || undefined,
      friendsGroupId: effectiveRoom?.friends_group_id || route.params?.friendsGroupId || undefined,
      friendsGroupName: effectiveRoom?.friends_group_name || route.params?.friendsGroupName || undefined,
    });
  }, [
    effectiveRoom?.family_id,
    effectiveRoom?.family_name,
    effectiveRoom?.friends_group_id,
    effectiveRoom?.friends_group_name,
    navigation,
    route.params?.familyId,
    route.params?.familyName,
    route.params?.friendsGroupId,
    route.params?.friendsGroupName,
    routeRoomId,
  ]);

  const handleStartNextGroupQuiz = useCallback(async () => {
    const didResetRoom = await resetRoom();

    if (!didResetRoom) {
      Alert.alert(
        t('results.errorLoadingResults', language),
        resetError || t('familiesFriends.playAnotherQuizError', language) || 'Unable to start a new quiz right now.',
      );
      return;
    }

    navigateToGameRoom();
  }, [language, navigateToGameRoom, resetError, resetRoom]);

  useEffect(() => {
    if (!routeRoomId) {
      return;
    }

    const roomChannel = supabaseLib
      .channel(`results_room_reset_${routeRoomId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'multiplayer_game_rooms',
          filter: `id=eq.${routeRoomId}`,
        },
        (payload) => {
          const nextRoom = payload.new as typeof effectiveRoom & {
            status?: string | null;
            current_session_id?: string | null;
          };
          const previousRoom = payload.old as typeof effectiveRoom & {
            status?: string | null;
          };

          if (nextRoom) {
            setHydratedRoom((currentHydratedRoom: any) => ({
              ...(currentHydratedRoom || {}),
              ...nextRoom,
            }));
          }

          if (
            nextRoom?.status === 'waiting' &&
            !nextRoom.current_session_id &&
            previousRoom?.status === 'completed'
          ) {
            navigateToGameRoom();
          }
        },
      )
      .subscribe();

    return () => {
      roomChannel.unsubscribe();
    };
  }, [navigateToGameRoom, routeRoomId]);

  // Optimized multiplayer results processing - faster and more reliable
  const processMultiplayerResults = async () => {
    const currentUserId = profile?.id;

    // CRITICAL: Set processing guard immediately
    if (isProcessingRewards) {
      return;
    }

    setIsProcessingRewards(true);
    setGamificationLoading(true);
    setGamificationError(null);

    // Early validation
    if (!currentUserId || !effectiveRoom || leaderboard.length === 0) {
      setGamificationError('Missing game data');
      setGamificationLoading(false);
      setIsProcessingRewards(false);
      return;
    }

    // Skip if already processed
    if (xpEarned > 0) {
      setGamificationLoading(false);
      setIsProcessingRewards(false);
      return;
    }

    // Reduced timeout for better UX
    const timeoutId = setTimeout(() => {
      setGamificationError('Calculation timed out. Using basic rewards.');
      setGamificationLoading(false);
      setIsProcessingRewards(false);
    }, 8000); // Reduced from 20s to 8s

    try {
      // Get player data quickly
      const currentPlayer = leaderboard.find((p: any) => p.user_id === currentUserId);
      if (!currentPlayer) {
        throw new Error('Player not found in leaderboard');
      }

      const playerScore = currentPlayer.score || 0;
      const playerRank = leaderboard.findIndex((p: any) => p.user_id === currentUserId) + 1;

      // Try fast batch processing ONLY (remove duplicate enhanced processing)
      let batchResult: any;
      try {
        // Use Promise.race for faster fallback
        batchResult = await Promise.race([
          batchProcessGamification({
            userId: currentUserId,
            score: playerScore,
            totalQuestions: 10,
            correctCount: playerScore, // playerScore IS raw correct count (useLocalMultiplayerQuiz.ts line 235)
            categoryId: typeof effectiveRoom.category_id === 'number' ? effectiveRoom.category_id : 1,
            difficulty: effectiveRoom.difficulty || 'medium',
            isFirstQuiz: false,
            gameType: 'multiplayer',
            gameContext: {
              rank: playerRank,
              totalPlayers: leaderboard.length,
              roomId: effectiveRoom.id,
              familyId: effectiveRoom.family_id || effectiveRoom.family_name
            }
          }),
          // Fallback after 5 seconds
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Batch processing timeout')), 5000)
          )
        ]);
      } catch (batchError) {
        console.error('Batch processing failed, using instant fallback:', batchError);
        
        // Fast fallback calculation
        const baseXP = Math.max(5, Math.floor(playerScore / 10));
        const rankBonus = playerRank === 1 ? 20 : playerRank <= 3 ? 10 : 5;
        const finalXP = baseXP + rankBonus;

        batchResult = {
          beforeXP: null,
          afterXP: { 
            id: 'fallback',
            user_id: currentUserId, 
            total_xp: finalXP, 
            current_level: 1, 
            xp_to_next_level: 100,
            rank: 'Beginner' as const,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          earnedXP: finalXP,
          levelUp: false,
          newLevel: 1,
          newAchievements: [],
          streakInfo: { 
            id: 'fallback',
            user_id: currentUserId, 
            current_streak: 1, 
            longest_streak: 1, 
            last_activity_date: new Date().toISOString(),
            grace_period_used: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        };
      }

      // PGS Layer 1: fire-and-forget, skip on fallback results
      if (batchResult && !isFallbackResult(batchResult)) {
        syncQuizToPGS({
          correctCount: playerScore, // raw correct count, no conversion
          totalQuestions: 10,
          categoryId: typeof effectiveRoom.category_id === 'number' ? effectiveRoom.category_id : 1,
          gameType: 'multiplayer',
          rank: playerRank,
        }).catch(() => {});
        // PGS Cloud Save: debounced auto-save
        triggerAutoSave(currentUserId).catch(() => {});
      }

      // Update state with results
      setBeforeXP(batchResult.beforeXP);
      setAfterXP(batchResult.afterXP);
      setXpEarned(batchResult.earnedXP || 0);
      setLevelUp(batchResult.levelUp || false);
      setNewLevel(batchResult.newLevel || 1);
      setNewRank(batchResult.afterXP?.rank || 'Beginner');
      setNewAchievements(batchResult.newAchievements || []);
      setStreakInfo(batchResult.streakInfo || null);

    } catch (error) {
      console.error('❌ [processMultiplayerResults] Error:', error);
      setGamificationError('Unable to calculate rewards');
    } finally {
      clearTimeout(timeoutId);
      setGamificationLoading(false);
      setIsProcessingRewards(false);
    }
  };

  const normalizedLeaderboard = useMemo<LeaderboardPlayerResult[]>(() => (
    leaderboard.map((player: any) => {
      const totalQuestions = typeof player.total_questions === "number" && player.total_questions > 0
        ? player.total_questions
        : 10;
      const rawCorrectCount = typeof player.score === "number" ? player.score : 0;
      const accuracy = totalQuestions > 0
        ? Math.round((rawCorrectCount / totalQuestions) * 100)
        : 0;

      return {
        ...player,
        score: rawCorrectCount,
        correct: rawCorrectCount,
        totalQuestions,
        accuracy,
        timeTakenSeconds: typeof player.time_taken_seconds === "number" ? player.time_taken_seconds : null,
        time: formatResultTime(typeof player.time_taken_seconds === "number" ? player.time_taken_seconds : null),
      };
    })
  ), [leaderboard]);

  const scores: { [user_id: string]: number } = {};
  for (const player of normalizedLeaderboard) {
    scores[player.user_id] = player.score;
  }

  const sortedLeaderboard = [...normalizedLeaderboard].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if ((a.timeTakenSeconds ?? Number.MAX_SAFE_INTEGER) !== (b.timeTakenSeconds ?? Number.MAX_SAFE_INTEGER)) {
      return (a.timeTakenSeconds ?? Number.MAX_SAFE_INTEGER) - (b.timeTakenSeconds ?? Number.MAX_SAFE_INTEGER);
    }
    return (a.name || a.nickname || '').localeCompare(b.name || b.nickname || '');
  });
  const currentPlayerResult = sortedLeaderboard.find((p) => p.user_id === profile.id);
  const currentPlayerPlace = currentPlayerResult
    ? sortedLeaderboard.findIndex((p) => p.user_id === profile.id) + 1
    : 0;
  const currentPlayerAccuracy =
    currentPlayerResult && typeof currentPlayerResult.accuracy === 'number'
      ? currentPlayerResult.accuracy
      : 0;
  const currentPlayerTotalQuestions = currentPlayerResult?.totalQuestions ?? 10;
  const totalPlayers = sortedLeaderboard.length;
  const winner = sortedLeaderboard.length > 0 ? sortedLeaderboard[0] : null;
  const isWinner = !!winner && winner.user_id === profile.id;
  const isTie =
    sortedLeaderboard.length > 1 &&
    sortedLeaderboard.every((player) => player.score === sortedLeaderboard[0].score);
  const compactGrowthHighlights = useMemo(() => {
    const highlights: Array<{
      key: string;
      icon: string;
      title: string;
      subtitle?: string;
      accentColor: string;
      backgroundColor: string;
    }> = [];

    if (xpEarned !== 0) {
      highlights.push({
        key: 'xp',
        icon: xpEarned > 0 ? '⭐' : '📉',
        title:
          xpEarned > 0
            ? `+${xpEarned} ${t('gamification.xp_short', language)}`
            : `${xpEarned} ${t('gamification.xp_short', language)}`,
        subtitle:
          afterXP?.total_xp !== undefined
            ? `${t('results.score', language)}: ${afterXP.total_xp} ${t('gamification.xp_short', language)}`
            : undefined,
        accentColor: xpEarned > 0 ? '#4F46E5' : '#DC2626',
        backgroundColor: xpEarned > 0 ? '#EEF2FF' : '#FEF2F2',
      });
    }

    if (levelUp) {
      highlights.push({
        key: 'level-up',
        icon: '🎊',
        title: t('results.levelUp', language),
        subtitle: `${t('results.player', language)} ${newLevel} · ${newRank}`,
        accentColor: '#7C3AED',
        backgroundColor: '#F3E8FF',
      });
    }

    if (streakInfo && streakInfo.current_streak > 0) {
      highlights.push({
        key: 'streak',
        icon: '🔥',
        title: `${streakInfo.current_streak} ${t('results.dayStreak', language)}`,
        subtitle:
          streakInfo.current_streak === streakInfo.longest_streak && streakInfo.current_streak > 1
            ? t('results.resultsSavedSuccessfully', language)
            : undefined,
        accentColor: '#EA580C',
        backgroundColor: '#FFF7ED',
      });
    }

    if (newAchievements.length > 0) {
      highlights.push({
        key: 'achievements',
        icon: '🏅',
        title: `${newAchievements.length} ${t('results.achievements', language)}`,
        subtitle: newAchievements[0]?.achievement?.name || undefined,
        accentColor: '#0F766E',
        backgroundColor: '#ECFDF5',
      });
    }

    return highlights.slice(0, 3);
  }, [afterXP?.total_xp, language, levelUp, newAchievements, newLevel, newRank, streakInfo, xpEarned]);

  // Confetti and sound state
  const [confetti, setConfetti] = useState(false);
  const soundRef = useRef<Audio.Sound | null>(null);
  const applauseRef = useRef<Audio.Sound | null>(null);

  // Trophy bounce animation
  const trophyAnim = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    if (isWinner) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(trophyAnim, {
            toValue: 1.25,
            duration: 350,
            useNativeDriver: Platform.OS !== "web",
            easing: Easing.out(Easing.ease),
          }),
          Animated.timing(trophyAnim, {
            toValue: 1,
            duration: 350,
            useNativeDriver: Platform.OS !== "web",
            easing: Easing.in(Easing.ease),
          }),
        ]),
      ).start();
    } else {
      trophyAnim.setValue(1);
    }
  }, [isWinner]);

  // Play celebration if winner (confetti, win sound) and applause for all
  useEffect(() => {
    if (isWinner) {
      setConfetti(true);
      (async () => {
        try {
          const { sound } = await Audio.Sound.createAsync(winSound as any);
          soundRef.current = sound;
          await sound.playAsync();
        } catch {
          // Ignore winner sound failures.
        }
      })();
    }
    (async () => {
      try {
        const { sound } = await Audio.Sound.createAsync(applauseSound as any);
        applauseRef.current = sound;
        await sound.playAsync();
      } catch {
        // Ignore applause sound failures.
      }
    })();
    return () => {
      soundRef.current?.unloadAsync();
      applauseRef.current?.unloadAsync();
    };
  }, [isWinner]);

  // Ask for a store rating after a win. Waits out the confetti and the win
  // sound above — the ask should land on the good feeling, not interrupt it.
  const reviewPromptedRef = useRef(false);
  useEffect(() => {
    if (reviewPromptedRef.current || totalPlayers === 0) return;

    const timer = setTimeout(() => {
      reviewPromptedRef.current = true;
      void maybeRequestPushAfterQuiz().then(() => maybeRequestReviewAfterMatch({ isWinner, isTie, totalPlayers }));
    }, 4000);

    return () => clearTimeout(timer);
  }, [isWinner, isTie, totalPlayers]);

  // Share results
  const handleShare = useCallback(async () => {
    try {
      const place =
        sortedLeaderboard.findIndex((p: any) => p.user_id === profile.id) + 1;
      const msg = t('results.multiplayerShareMessage', language)
        .replace('{{score}}', scores[profile.id]?.toString() || '0')
        .replace('{{place}}', place.toString());
      await Share.share({ message: msg });
    } catch (e) {
      Alert.alert(t('results.shareFailed', language));
    }
  }, [sortedLeaderboard, profile.id, scores]);

  // Error message state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Back to Lobby handler (should only be used for explicit user action, NOT for rematch flow)
  const handleBackToLobby = useCallback(() => {
    navigation.reset({
      index: 0,
      routes: [{
        name: "Together",
        params: {
          screen: roomMeta.groupType === 'friends' ? 'FriendsHub' : 'FamilyHub',
          params: {
            screen: 'MultiplayerLobby',
            params: {
              familyId: effectiveRoom?.family_id || null,
              familyName: effectiveRoom?.family_name || null,
              friendsGroupId: effectiveRoom?.friends_group_id || null,
              friendsGroupName: effectiveRoom?.friends_group_name || null,
              groupType: roomMeta.groupType,
            },
          },
        },
      }],
    } as never);
  }, [effectiveRoom?.family_id, effectiveRoom?.family_name, effectiveRoom?.friends_group_id, effectiveRoom?.friends_group_name, navigation, roomMeta.groupType]);
  // NOTE: All rematch navigation should always go to the new Game Room, never to the MultiplayerLobby, unless the user explicitly chooses to leave.
  const showError =
    errorMessage &&
    !errorMessage.toLowerCase().includes("already been completed");

  // Development logging (removed to reduce console noise)
  // Asset validation happens at import time, no need to log here

  // 1. Determine result type
  const isGameOver = !isWinner && !isTie;

  // 2. Dynamic header and verse
  const headerText = isTie
    ? t('results.itsATie', language)
    : isWinner
      ? t('results.youWon', language)
      : winner && winner.name
        ? t('results.wonTheGame', language).replace('{{name}}', winner.name)
        : t('results.gameOver', language);
  const subHeaderText = isTie
    ? t('results.wellPlayedBoth', language)
    : isWinner
      ? t('results.congratulations', language)
      : t('results.betterLuckNextTime', language);
  const verseLineDynamic = isTie
    ? t('results.verseSpurOneAnother', language)
    : isWinner
      ? t('results.verseFightGoodFight', language)
      : t('results.verseBeStrong', language);

  // 3. Trophy animation source
  const trophySource = isTie
    ? trophyLotties["tie"]
    : isWinner
      ? trophyLotties["winner"]
      : trophyLotties["loser"];

  // 4. Confetti only for win/tie
  const showConfetti = isWinner || isTie;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: IQES_RESULTS.background }} data-oid="6k8uh6f">
      <View style={{ flex: 1 }}>
        <View pointerEvents="none" style={styles.ambientOrbTop} />
        <View pointerEvents="none" style={styles.ambientOrbBottom} />
        <View pointerEvents="none" style={styles.sparkOne} />
        <View pointerEvents="none" style={styles.sparkTwo} />
        {/* Confetti overlay for extra effect if needed */}
        {showConfetti && <Confetti trigger={showConfetti} />}
        <ResultsConfettiOverlay showConfetti={showConfetti} confettiLottie={confettiLottie} />
        {/* Mobile-optimized content wrapper - direct ScrollView without constraints */}
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            alignItems: 'center',
            paddingHorizontal: getResponsivePadding(),
            paddingTop: isSmallScreen ? 16 : 32,
            paddingBottom: isSmallScreen ? 100 : 120, // Even more bottom padding
          }}
          showsVerticalScrollIndicator={false}
          bounces={true}
          data-oid="5t_mdds"
        >
              {/* Header */}
              <ResultsHeaderBlock
                trophySource={trophySource}
                trophyMaxHeight={trophyMaxHeight}
                headerText={headerText}
                subHeaderText={subHeaderText}
                verseLineDynamic={verseLineDynamic}
                eyebrowText={`${roomMeta.groupLabel} ${t('results.quizComplete', language)}`}
                contextTitle={roomMeta.groupName}
              />

              <View style={{
                width: '100%',
                backgroundColor: IQES_RESULTS.panel,
                borderRadius: isSmallScreen ? 20 : 24,
                borderWidth: 1,
                borderColor: IQES_RESULTS.border,
                paddingVertical: isSmallScreen ? 16 : 20,
                paddingHorizontal: isSmallScreen ? 14 : 18,
                marginBottom: isSmallScreen ? 14 : 18,
              }}>
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12,
                }}>
                  <View style={{ flex: 1, marginRight: 10 }}>
                    <Text style={{
                      fontSize: scaleFont(12),
                      fontWeight: '800',
                      letterSpacing: 0.8,
                      textTransform: 'uppercase',
                      color: IQES_RESULTS.goldSoft,
                      marginBottom: 4,
                    }}>
                      {t('results.yourResult', language) || 'Your result'}
                    </Text>
                    <Text style={{
                      fontSize: scaleFont(18),
                      fontWeight: '800',
                      color: IQES_RESULTS.ivory,
                      lineHeight: scaleFont(22),
                      marginBottom: 4,
                    }}>
                      {currentPlayerPlace > 0
                        ? `${t('results.place', language)} #${currentPlayerPlace}`
                        : t('results.quizComplete', language)}
                    </Text>
                    <Text style={{
                      fontSize: scaleFont(13),
                      color: IQES_RESULTS.muted,
                      lineHeight: scaleFont(18),
                    }}>
                      {currentPlayerResult
                        ? `${currentPlayerResult.correct}/${currentPlayerTotalQuestions} ${t('multiplayer_waiting.correct', language).toLowerCase()} · ${currentPlayerAccuracy}% ${t('multiplayer_waiting.accuracy', language).toLowerCase()}`
                        : subHeaderText}
                    </Text>
                  </View>
                  <View style={{
                    backgroundColor: IQES_RESULTS.purpleDeep,
                    borderWidth: 1,
                    borderColor: IQES_RESULTS.border,
                    borderRadius: 999,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    alignSelf: 'flex-start',
                  }}>
                    <Text style={{
                      fontSize: scaleFont(12),
                      fontWeight: '700',
                      color: IQES_RESULTS.goldSoft,
                    }}>
                      {roomMeta.groupLabel}
                    </Text>
                  </View>
                </View>
                <View style={{
                  flexDirection: 'row',
                  gap: 10,
                  marginBottom: 10,
                }}>
                  <View style={{
                    flex: 1,
                    backgroundColor: "rgba(248, 200, 78, 0.14)",
                    borderWidth: 1,
                    borderColor: IQES_RESULTS.border,
                    borderRadius: 18,
                    paddingVertical: 14,
                    paddingHorizontal: 12,
                  }}>
                    <Text style={{
                      fontSize: scaleFont(12),
                      fontWeight: '700',
                      color: IQES_RESULTS.muted,
                      marginBottom: 4,
                    }}>
                      {t('results.place', language)}
                    </Text>
                    <Text style={{
                      fontSize: scaleFont(24),
                      fontWeight: '800',
                      color: IQES_RESULTS.goldSoft,
                    }}>
                      {currentPlayerPlace > 0 ? `#${currentPlayerPlace}` : '--'}
                    </Text>
                  </View>
                  <View style={{
                    flex: 1,
                    backgroundColor: IQES_RESULTS.panelLift,
                    borderWidth: 1,
                    borderColor: "rgba(255, 247, 223, 0.1)",
                    borderRadius: 18,
                    paddingVertical: 14,
                    paddingHorizontal: 12,
                  }}>
                    <Text style={{
                      fontSize: scaleFont(12),
                      fontWeight: '700',
                      color: IQES_RESULTS.muted,
                      marginBottom: 4,
                    }}>
                      {t('results.accuracy', language) || 'Accuracy'}
                    </Text>
                    <Text style={{
                      fontSize: scaleFont(24),
                      fontWeight: '800',
                      color: IQES_RESULTS.ivory,
                    }}>
                      {currentPlayerAccuracy}%
                    </Text>
                  </View>
                </View>
                <View style={{
                  backgroundColor: IQES_RESULTS.panelDeep,
                  borderRadius: 20,
                  paddingVertical: 14,
                  paddingHorizontal: 16,
                  borderWidth: 1,
                  borderColor: IQES_RESULTS.border,
                }}>
                  <Text style={{
                    fontSize: scaleFont(12),
                    fontWeight: '800',
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                    color: IQES_RESULTS.goldSoft,
                    marginBottom: 6,
                  }}>
                    {t('results.score', language)}
                  </Text>
                  <Text style={{
                    fontSize: scaleFont(28),
                    fontWeight: '800',
                    color: IQES_RESULTS.ivory,
                    lineHeight: scaleFont(32),
                  }}>
                    {currentPlayerResult?.correct ?? 0} / {currentPlayerTotalQuestions}
                  </Text>
                  {currentPlayerResult?.time ? (
                    <Text style={{
                      fontSize: scaleFont(13),
                      color: IQES_RESULTS.muted,
                      marginTop: 6,
                    }}>
                      {`${t('results.time', language) || 'Time'} ${currentPlayerResult.time}`}
                    </Text>
                  ) : null}
                </View>
              </View>

              <View style={{
                width: '100%',
                backgroundColor: IQES_RESULTS.panel,
                borderRadius: isSmallScreen ? 20 : 24,
                borderWidth: 1,
                borderColor: IQES_RESULTS.border,
                paddingVertical: isSmallScreen ? 18 : 24,
                paddingHorizontal: isSmallScreen ? 12 : 16,
                marginBottom: isSmallScreen ? 14 : 18,
              }}>
                <View style={{
                  width: '100%',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                  paddingHorizontal: isSmallScreen ? 4 : 2,
                }}>
                  <View style={{ flex: 1, marginRight: 10 }}>
                    <Text style={{
                      fontSize: scaleFont(12),
                      fontWeight: '800',
                      letterSpacing: 0.8,
                      textTransform: 'uppercase',
                      color: IQES_RESULTS.goldSoft,
                      marginBottom: 4,
                    }}>
                      {t('results.leaderboard', language)}
                    </Text>
                    <Text style={{
                      fontSize: scaleFont(15),
                      color: IQES_RESULTS.muted,
                      lineHeight: scaleFont(20),
                    }}>
                      {t('results.quizComplete', language)}
                    </Text>
                  </View>
                  <View style={{
                    backgroundColor: IQES_RESULTS.panelLift,
                    borderWidth: 1,
                    borderColor: "rgba(255, 247, 223, 0.1)",
                    borderRadius: 999,
                    paddingHorizontal: 10,
                    paddingVertical: 6,
                  }}>
                    <Text style={{
                      fontSize: scaleFont(11),
                      fontWeight: '700',
                      color: IQES_RESULTS.muted,
                    }}>
                      {totalPlayers}
                    </Text>
                  </View>
                </View>
                {isLoadingResults && sortedLeaderboard.length === 0 ? (
                  <View style={{
                    padding: isSmallScreen ? 8 : 12,
                    alignItems: 'center',
                    width: '100%'
                  }}>
                    <ActivityIndicator
                      size={isSmallScreen ? "small" : "large"}
                      color={IQES_RESULTS.gold}
                      style={{ marginBottom: isSmallScreen ? 12 : 16 }}
                    />
                    <Text style={{
                      fontSize: scaleFont(18),
                      color: IQES_RESULTS.goldSoft,
                      fontWeight: 'bold',
                      textAlign: 'center',
                      lineHeight: scaleFont(22)
                    }}>
                      {t('results.waitingForResults', language)}
                    </Text>
                  </View>
                ) : fetchError && sortedLeaderboard.length === 0 ? (
                  <View style={{
                    padding: isSmallScreen ? 8 : 12,
                    alignItems: 'center',
                    width: '100%'
                  }}>
                    <Text style={{
                      fontSize: scaleFont(18),
                      color: IQES_RESULTS.red,
                      fontWeight: 'bold',
                      textAlign: 'center',
                      marginBottom: 8,
                      lineHeight: scaleFont(22)
                    }}>
                      {t('results.errorLoadingResults', language)}
                    </Text>
                    <Text style={{
                      fontSize: scaleFont(15),
                      color: IQES_RESULTS.muted,
                      textAlign: 'center',
                      marginBottom: 12,
                      lineHeight: scaleFont(20)
                    }}>
                      {fetchError}
                    </Text>
                    <TouchableOpacity
                      onPress={handleRetryFetch}
                      style={{
                        backgroundColor: IQES_RESULTS.gold,
                        paddingVertical: isSmallScreen ? 12 : 14,
                        paddingHorizontal: isSmallScreen ? 20 : 24,
                        borderRadius: 999,
                        minHeight: 44 // Minimum touch target
                      }}
                    >
                      <Text style={{
                        color: IQES_RESULTS.purpleDeep,
                        fontWeight: 'bold',
                        fontSize: scaleFont(16),
                        textAlign: 'center'
                      }}>
                        {t('results.retryLoadResults', language)}
                      </Text>
                    </TouchableOpacity>
                  </View>
                ) : sortedLeaderboard.length === 0 ? (
                  <View style={{
                    padding: isSmallScreen ? 8 : 12,
                    alignItems: 'center',
                    width: '100%'
                  }}>
                    <Text style={{
                      fontSize: scaleFont(18),
                      color: IQES_RESULTS.goldSoft,
                      fontWeight: 'bold',
                      textAlign: 'center',
                      lineHeight: scaleFont(22)
                    }}>
                      {t('results.noPlayersFound', language)}
                    </Text>
                    <Text style={{
                      fontSize: scaleFont(15),
                      color: IQES_RESULTS.muted,
                      textAlign: 'center',
                      marginTop: 8,
                      lineHeight: scaleFont(20)
                    }}>
                      {t('results.fetchingResults', language)}
                    </Text>
                  </View>
                ) : (
                  <ResultsLeaderboard
                    sortedLeaderboard={sortedLeaderboard}
                    profile={profile}
                    badgeLotties={badgeLotties}
                  />
                )}
              </View>
              <ResultsActionsBlock
                mode="multi"
                onPlayAnotherQuiz={handleStartNextGroupQuiz}
                onSecondaryAction={handleBackToLobby}
                showPrimaryAction={isCurrentUserHost}
                eyebrowText={roomMeta.groupLabel}
                titleText={
                  isCurrentUserHost
                    ? (t('results.startNextGroupTitle', language)?.replace('{{groupType}}', roomMeta.groupLabelLower) || `Play another ${roomMeta.groupLabelLower} quiz`)
                    : (t('results.waitingForNextGroupTitle', language)?.replace('{{groupType}}', roomMeta.groupLabelLower) || `Waiting for the next ${roomMeta.groupLabelLower} quiz`)
                }
                subtitleText={
                  isCurrentUserHost
                    ? (t('results.startNextGroupSubtitle', language)?.split('{{groupType}}').join(roomMeta.groupLabelLower) || `Start a new round with the same ${roomMeta.groupLabelLower} group, or go back to the ${roomMeta.groupLabelLower} lobby.`)
                    : (t('results.waitingForNextGroupSubtitle', language)?.replace('{{groupType}}', roomMeta.groupLabelLower) || `The host can start another round. You can return to the ${roomMeta.groupLabelLower} lobby while you wait.`)
                }
                helperText={
                  isCurrentUserHost
                    ? undefined
                    : (t('results.waitForHostNextGroup', language) || 'When the host starts another quiz, everyone will return to the room automatically.')
                }
                primaryText={t('results.startNewGroupQuiz', language)?.replace('{{groupType}}', roomMeta.groupLabelLower) || `Start new ${roomMeta.groupLabelLower} quiz`}
                secondaryText={t('results.backToGroupLobby', language)?.replace('{{groupType}}', roomMeta.groupLabelLower) || `Back to ${roomMeta.groupLabelLower} lobby`}
              />
              {/* Gamification Loading - Mobile Optimized */}
              {gamificationLoading && (
                <View style={{
                  backgroundColor: IQES_RESULTS.panel,
                  borderRadius: isSmallScreen ? 16 : 20,
                  padding: isSmallScreen ? 16 : 24,
                  marginTop: isSmallScreen ? 12 : 16,
                  marginBottom: isSmallScreen ? 12 : 16,
                  width: '100%',
                  borderWidth: 1,
                  borderColor: IQES_RESULTS.border,
                  shadowColor: '#1D1A23',
                  shadowOffset: { width: 0, height: 1 },
                  shadowOpacity: 0.06,
                  shadowRadius: 2,
                  elevation: 1,
                }}>
                  <Text style={{
                    textAlign: 'center',
                    color: IQES_RESULTS.muted,
                    fontSize: scaleFont(16),
                    lineHeight: scaleFont(20),
                  }}>
                    {t('results.fetchingResults', language)}
                  </Text>
                </View>
              )}

              {/* Gamification Error - Mobile Optimized */}
              {gamificationError && (
                <View style={{
                  backgroundColor: "rgba(239, 107, 99, 0.15)",
                  borderRadius: isSmallScreen ? 16 : 20,
                  padding: isSmallScreen ? 12 : 16,
                  marginTop: isSmallScreen ? 12 : 16,
                  marginBottom: isSmallScreen ? 12 : 16,
                  width: '100%',
                  borderWidth: 1,
                  borderColor: "rgba(239, 107, 99, 0.35)",
                }}>
                  <Text style={{
                    textAlign: 'center',
                      color: IQES_RESULTS.red,
                    fontWeight: '600',
                    marginBottom: 8,
                    fontSize: scaleFont(14),
                    lineHeight: scaleFont(18),
                  }}>
                    ⚠️ {gamificationError}
                  </Text>
                  <TouchableOpacity
                    style={{
                        backgroundColor: IQES_RESULTS.red,
                      borderRadius: isSmallScreen ? 6 : 8,
                      paddingVertical: isSmallScreen ? 10 : 12,
                      paddingHorizontal: isSmallScreen ? 16 : 20,
                      alignSelf: 'center',
                      minHeight: 44,
                    }}
                    onPress={processMultiplayerResults}
                  >
                    <Text style={{
                      color: 'white',
                      fontWeight: '600',
                      textAlign: 'center',
                      fontSize: scaleFont(14),
                    }}>
                      🔄 Retry
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
              {/* Compact Growth Summary */}
              {!gamificationLoading && !gamificationError && compactGrowthHighlights.length > 0 && (
                <View style={{
                  width: '100%',
                  backgroundColor: IQES_RESULTS.panel,
                  borderRadius: isSmallScreen ? 20 : 24,
                  borderWidth: 1,
                  borderColor: IQES_RESULTS.border,
                  paddingTop: isSmallScreen ? 16 : 20,
                  paddingBottom: isSmallScreen ? 14 : 18,
                  marginTop: isSmallScreen ? 8 : 12,
                  marginBottom: isSmallScreen ? 14 : 18,
                }}>
                  <Text style={{
                    fontSize: scaleFont(12),
                    fontWeight: '800',
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                    color: IQES_RESULTS.goldSoft,
                    marginBottom: 6,
                    paddingHorizontal: isSmallScreen ? 14 : 18,
                  }}>
                    {t('results.rewardsEarned', language)}
                  </Text>
                  <Text style={{
                    fontSize: scaleFont(15),
                    color: IQES_RESULTS.muted,
                    paddingHorizontal: isSmallScreen ? 14 : 18,
                    marginBottom: 12,
                    lineHeight: scaleFont(20),
                  }}>
                    {t('results.resultsSavedSuccessfully', language)}
                  </Text>
                  <View style={{
                    paddingHorizontal: isSmallScreen ? 14 : 18,
                    gap: 10,
                  }}>
                    {compactGrowthHighlights.map((highlight) => (
                      <View
                        key={highlight.key}
                        style={{
                          backgroundColor: IQES_RESULTS.panelLift,
                          borderWidth: 1,
                          borderColor: IQES_RESULTS.border,
                          borderRadius: 18,
                          paddingVertical: 14,
                          paddingHorizontal: 14,
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      >
                        <Text style={{
                          fontSize: scaleFont(24),
                          marginRight: 12,
                        }}>
                          {highlight.icon}
                        </Text>
                        <View style={{ flex: 1 }}>
                          <Text style={{
                            fontSize: scaleFont(18),
                            fontWeight: '800',
                            color: highlight.accentColor,
                            marginBottom: highlight.subtitle ? 2 : 0,
                          }}>
                            {highlight.title}
                          </Text>
                          {highlight.subtitle ? (
                            <Text style={{
                              fontSize: scaleFont(13),
                              color: IQES_RESULTS.muted,
                              lineHeight: scaleFont(18),
                            }}>
                              {highlight.subtitle}
                            </Text>
                          ) : null}
                        </View>
                      </View>
                    ))}
                  </View>
                </View>
              )}
              {/* Footer - Mobile Optimized */}
              <View style={{
                width: '100%',
                alignItems: 'center',
                marginTop: isSmallScreen ? 16 : 24,
                paddingBottom: isSmallScreen ? 8 : 16
              }} data-oid="5v7k4n5">
                <Text style={{
                  fontSize: scaleFont(16),
                  color: IQES_RESULTS.muted,
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  Kingdom Bible Quiz
                </Text>
              </View>
            </ScrollView>
        </View>
    </SafeAreaView>
  );
}
const styles = StyleSheet.create({
  ambientOrbTop: {
    position: "absolute",
    top: -120,
    right: -105,
    width: 250,
    height: 250,
    borderRadius: 125,
    backgroundColor: "rgba(91, 44, 131, 0.38)",
  },
  ambientOrbBottom: {
    position: "absolute",
    bottom: -155,
    left: -125,
    width: 270,
    height: 270,
    borderRadius: 135,
    backgroundColor: "rgba(248, 200, 78, 0.11)",
  },
  sparkOne: {
    position: "absolute",
    top: "23%",
    right: "18%",
    width: 5,
    height: 5,
    borderRadius: 3,
    backgroundColor: IQES_RESULTS.goldSoft,
    opacity: 0.72,
  },
  sparkTwo: {
    position: "absolute",
    bottom: "26%",
    left: "23%",
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: IQES_RESULTS.gold,
    opacity: 0.55,
  },
});
export default MultiplayerResultsScreen;
